import { ChangeDetectionStrategy, AfterViewInit, Component, computed, inject, input, OnChanges, output, signal, SimpleChanges } from '@angular/core';
import { LoggerService } from '@/core/services/logger.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { ConsultorioDadosGeriasComponent } from './consultorio-dados-gerais/consultorio-dados-gerias';
import { ConsultorioCompartilhamentoComponent } from './consultorio-compartilhamento/consultorio-compartilhamento';
import { ConsultorioEspecialidadeComponent } from './consultorio-especialidade/consultorio-especialidade';
import { ConsultorioAnotacoesComponent } from './consultorio-anotacoes/consultorio-anotacoes';
import { ConsultorioHistoricoComponent } from './consultorio-historico/consultorio-historico';
import { ConsultorioHorarioComponent } from './consultorio-horario/consultorio-horario';
import { ConsultorioTelefoneComponent } from './consultorio-telefone/consultorio-telefone';
import { ConsultorioCreateDialogComponent } from './consultorio-create-dialog/consultorio-create-dialog';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { TabContainer, TabContainerOptionType, TabContent } from '@/shared/components/layout/tab-container/tab-container';
import { BotaoAcoes, BotaoAcoesType } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { ActionContainer } from '@/shared/components/containers/action-container';
import { ConsultorioPrivadoService } from '@/core/api/sicom-api/registro/medico/consultorio-privado/consultorio-privado.service';
import { MedicoService } from '@/core/api/sicom-api/registro/medico/medico.service';
import { ConsultorioPrivadoResponse } from '@/core/api/sicom-api/registro/medico/consultorio-privado/consultorio-privado.model';
import { ConsultorioListaComponent } from './consultorio-lista/consultorio-lista.component';
import { MenuItem } from 'primeng/api';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

@Component({
    selector: 'app-dashboard-consultorio',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        Button,
        ConsultorioDadosGeriasComponent,
        ConsultorioCompartilhamentoComponent,
        ConsultorioEspecialidadeComponent,
        ConsultorioAnotacoesComponent,
        ConsultorioHistoricoComponent,
        ConsultorioHorarioComponent,
        ConsultorioTelefoneComponent,
        TabContainer,
        TabContent,
        BotaoAcoes,
        ConsultorioCreateDialogComponent,
        ConsultorioListaComponent,
        CardContent,
        CanDirective
    ],
    templateUrl: './dashboard-consultorio.html',
    styleUrl: './dashboard-consultorio.scss'
})
/** Painel do consultório privado: navega entre registros e organiza as abas. */
export class DashboardConsultorio extends ActionContainer implements OnChanges, AfterViewInit {
    private readonly logger = inject(LoggerService);
    protected readonly roleConsultorioEditar = PERMISSOES.medicos.consultorio.editar;
    private readonly allOptions: TabContainerOptionType[] = [
        { label: 'Dados Gerais', value: 'dados-gerais', icon: 'pi pi-info-circle' },
        { label: 'Compartilhamento', value: 'compartilhamento', icon: 'pi pi-users' },
        { label: 'Especialidade', value: 'especialidade', icon: 'pi pi-th-large' },
        { label: 'Anotações', value: 'anotacoes', icon: 'pi pi-history' },
        { label: 'Histórico', value: 'historico', icon: 'pi pi-history' },
        { label: 'Horário', value: 'horario-funcionamento', icon: 'pi pi-clock' },
        { label: 'Telefone', value: 'telefone', icon: 'pi pi-phone' }
    ] satisfies TabContainerOptionType[];

    options: TabContainerOptionType[] = [...this.allOptions];
    actions: { [ind: string]: BotaoAcoesType & { useEdit: boolean } } = {};
    private readonly acaoNovoConsultorio: MenuItem = {
        label: 'Novo',
        icon: 'pi pi-plus',
        styleClass: 'bg-green-500',
        command: () => this.abrirNovoDialog()
    };
    /**
     * "Editar" saiu do menu de ações PRÓPRIO do `app-tab-container` e veio para o de cima: eram dois
     * botões "Ações" na mesma tela, e o de baixo tinha esse único item.
     *
     * `modoEdicao` do container é `model`, então abrir a edição daqui é escrever nele — o container
     * segue dono de Salvar/Cancelar, que continuam onde estavam.
     */
    private readonly acaoEditarConsultorio: MenuItem = {
        label: 'Editar',
        icon: 'pi pi-pencil',
        styleClass: 'bg-blue-500',
        command: () => this.habilitarEdicaoDaAba()
    };
    acoesNovo: MenuItem[] = [this.acaoNovoConsultorio];

    /** Edição da aba corrente, em duas mãos com o `app-tab-container` (`[(modoEdicao)]`). */
    edicaoAberta = false;

    tabsForEditButton = new Set<string>([this.allOptions[0].value]);
    selectedValue: string = this.allOptions[0].value;
    loadActions = false;
    saveDadosGerais = false;
    cancelDadosGerais = false;
    enableDadosGerais = false;
    mostrandoTelaCadastro = false;

    consultorios = signal<ConsultorioPrivadoResponse[]>([]);
    selectedDetail: ConsultorioPrivadoResponse | null = null;
    currentIndex = signal(0);
    pessoa = input<Pessoa>();
    modoSomenteLeitura = input(false);
    currentIndexChange = output<number>();
    consultorioChange = output<ConsultorioPrivadoResponse>();

    private readonly consultorioService = inject(ConsultorioPrivadoService);
    private readonly medicoService = inject(MedicoService);

    /** Código da situação do médico (ex.: 'A' = REGULAR); undefined enquanto não carregado. */
    protected readonly situacaoMedico = signal<string | undefined>(undefined);
    /** Só médico REGULAR ('A') pode incluir consultório. Antes de carregar, não bloqueia (backend valida). */
    protected readonly medicoRegular = computed(() => {
        const s = this.situacaoMedico();
        return s == null ? true : s.trim().toUpperCase() === 'A';
    });
    loadingList = signal(false);
    erroList = signal<string | undefined>(undefined);

    ngOnInit() {
        this.tabChange(this.selectedValue);
    }

    onChange(): void {
        this.loadActions = true;
    }

    ngAfterViewInit(): void {
        this.loadActions = true;
    }

    get selectedLabel(): string {
        const found = this.options.find((o) => o.value === this.selectedValue);
        return found?.label ?? '';
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['pessoa']) {
            const p = this.pessoa();
            if (p && typeof p.id === 'number' && !!p.uf) {
                this.loadList(p);
                this.carregarSituacaoMedico(p);
            } else {
                this.consultorios.set([]);
                this.currentIndex.set(0);
                this.situacaoMedico.set(undefined);
            }
        }
        if (changes['modoSomenteLeitura']) {
            if (this.modoSomenteLeitura()) {
                this.mostrandoTelaCadastro = false;
            }
            this.buildTabActions(this.selectedValue);
            this.atualizarAcoesMenuTopo();
        }
    }

    get total(): number {
        return this.consultorios().length;
    }

    get hasPrev(): boolean {
        return this.total > 0 && this.currentIndex() > 0;
    }

    get hasNext(): boolean {
        return this.total > 0 && this.currentIndex() < this.total - 1;
    }

    get positionLabel(): string {
        if (this.total <= 0) return 'Nenhum registro';
        return `Registro ${this.currentIndex() + 1} de ${this.total}`;
    }

    prev(): void {
        if (this.hasPrev) {
            this.currentIndex.update((v) => v - 1);
            this.emitChanges();
        }
    }

    next(): void {
        if (this.hasNext) {
            this.currentIndex.update((v) => v + 1);
            this.emitChanges();
        }
    }

    private clampIndex(): void {
        if (this.total === 0) {
            this.currentIndex.set(0);
            return;
        }
        if (this.currentIndex() < 0) this.currentIndex.set(0);
        if (this.currentIndex() > this.total - 1) this.currentIndex.set(this.total - 1);
    }

    private emitChanges(): void {
        this.currentIndexChange.emit(this.currentIndex());
        if (this.total > 0) {
            this.consultorioChange.emit(this.consultorios()[this.currentIndex()]);
        }
    }

    get selectedConsultorioId(): number | null {
        if (this.selectedDetail && typeof this.selectedDetail.idConsultorio === 'number') {
            return this.selectedDetail.idConsultorio;
        }
        if (this.total === 0) return null;
        const item = this.consultorios()[this.currentIndex()];
        return typeof item?.idConsultorio === 'number' ? item.idConsultorio : null;
    }

    private loadList(p: Pessoa): void {
        this.loadingList.set(true);
        this.erroList.set(undefined);
        this.consultorioService.listar({ id: p.id, uf: p.uf }).subscribe({
            next: (lista) => {
                this.consultorios.set(Array.isArray(lista) ? lista : []);
                this.clampIndex();
                this.emitChanges();
            },
            error: (err) => {
                this.logger.error('Erro ao listar consultórios privados', err);
                this.consultorios.set([]);
                this.currentIndex.set(0);
                this.erroList.set('Falha ao carregar consultórios.');
            },
            complete: () => this.loadingList.set(false)
        });
    }

    onConsultorioCreated(): void {
        const p = this.pessoa();
        if (!p || typeof p.id !== 'number' || !p.uf) return;

        this.loadingList.set(true);
        this.consultorioService.listar({ id: p.id, uf: p.uf }).subscribe({
            next: (lista) => {
                this.consultorios.set(Array.isArray(lista) ? lista : []);
                this.currentIndex.set(Math.max(0, this.consultorios().length - 1));
                this.emitChanges();
                this.mostrandoTelaCadastro = false;
                this.selectedDetail = null;
                this.redefinirAbasPadrao();
            },
            error: () => {},
            complete: () => this.loadingList.set(false)
        });
    }

    tabChange(tab: string): void {
        const tabValida = this.options.some((option) => option.value === tab) ? tab : (this.options[0]?.value ?? '');
        if (!tabValida) return;

        this.selectedValue = tabValida;
        this.setIsEditTab(tabValida);
        this.buildTabActions(tabValida);
        this.atualizarAcoesMenuTopo();
    }

    abrirNovoDialog(): void {
        if (this.modoSomenteLeitura()) return;
        // Não permite incluir consultório para médico não REGULAR (inativo/falecido/etc.).
        if (!this.medicoRegular()) return;
        this.selectedDetail = null;
        this.redefinirAbasPadrao();
        this.mostrandoTelaCadastro = true;
    }

    fecharTelaCadastro(): void {
        this.mostrandoTelaCadastro = false;
    }

    onRowClick(row: ConsultorioPrivadoResponse): void {
        this.selectedDetail = row;
        this.aplicarVisibilidadeAbas(row);
        this.tabChange('dados-gerais');
    }

    voltarParaLista(): void {
        this.selectedDetail = null;
        this.redefinirAbasPadrao();
    }

    override getActions(): { [ind: string]: BotaoAcoesType & { useEdit: boolean } } {
        return this.actions;
    }

    override setAction(actionIndex: string, action: BotaoAcoesType & { useEdit: boolean }): void {
        this.actions[actionIndex] = action;
        if (actionIndex === this.selectedValue) {
            this.atualizarAcoesMenuTopo();
        }
    }

    override getOptions(): TabContainerOptionType[] {
        return this.options;
    }

    override setOption(opt: TabContainerOptionType, tabIndex: number): void {
        this.options[tabIndex] = opt;
    }

    private aplicarVisibilidadeAbas(consultorio: ConsultorioPrivadoResponse): void {
        const ocultarCompartilhamento = consultorio.inUso === 'E';
        const ocultarEspecialidade = consultorio.inEspecializado === 'N';

        this.options = this.allOptions.filter((option) => {
            if (option.value === 'compartilhamento') return !ocultarCompartilhamento;
            if (option.value === 'especialidade') return !ocultarEspecialidade;
            return true;
        });

        if (!this.options.some((option) => option.value === this.selectedValue)) {
            this.selectedValue = 'dados-gerais';
        }
    }

    private redefinirAbasPadrao(): void {
        this.options = [...this.allOptions];
        if (!this.options.some((option) => option.value === this.selectedValue)) {
            this.selectedValue = this.options[0]?.value ?? '';
        }
        this.atualizarAcoesMenuTopo();
    }

    /** Carrega a situação do médico para gatear a inclusão de consultório; reconstrói o menu ao concluir. */
    private carregarSituacaoMedico(p: Pessoa): void {
        this.medicoService.buscar({ id: p.id, uf: p.uf }).subscribe({
            next: (medico) => {
                this.situacaoMedico.set(medico?.inSituacao ?? undefined);
                this.atualizarAcoesMenuTopo();
            },
            error: (err) => {
                this.logger.error('Erro ao carregar situação do médico', err);
                this.situacaoMedico.set(undefined);
            }
        });
    }

    private atualizarAcoesMenuTopo(): void {
        if (this.modoSomenteLeitura()) {
            this.acoesNovo = [];
            return;
        }

        const acoesTabAtual = this.getTabActions(this.selectedValue) ?? [];
        const acoesClonadas = acoesTabAtual.map((acao) => ({ ...acao }));
        const acaoNovo = { ...this.acaoNovoConsultorio, disabled: !this.medicoRegular() };

        // "Editar" só entra enquanto a edição não começou — aberta, quem manda são Salvar/Cancelar do
        // container, e o item aqui ofereceria uma ação sem efeito. Fora das abas editáveis ele fica
        // DESABILITADO em vez de sumir: item que desaparece ao trocar de aba se lê como defeito.
        const acaoEditar = this.edicaoAberta ? [] : [{ ...this.acaoEditarConsultorio, disabled: !this.isEditTab }];

        this.acoesNovo = [acaoNovo, ...acaoEditar, ...acoesClonadas];
    }

    /**
     * Abre a edição da aba corrente a partir do menu de cima. Guarda a mesma regra que apaga o item:
     * `disabled` é aparência, e chamada por outro caminho abriria a edição numa aba que não edita.
     */
    private habilitarEdicaoDaAba(): void {
        if (!this.isEditTab || this.modoSomenteLeitura()) {
            return;
        }

        // Duas coisas, e não uma: `modoEdicao` troca os botões do container para Salvar/Cancelar, e
        // `enableDadosGerais` é quem destrava o formulário da aba. O `habilitarEdicao()` do container
        // fazia as duas (a segunda pelo output `edicaoEnabled`); entrando por aqui, ambas são nossas.
        this.edicaoAberta = true;
        this.enableDadosGerais = true;
        this.atualizarAcoesMenuTopo();
    }

    /** O container devolve o estado ao salvar/cancelar; o menu precisa reagir para reoferecer "Editar". */
    aoMudarModoEdicao(aberta: boolean): void {
        this.edicaoAberta = aberta;
        this.atualizarAcoesMenuTopo();
    }
}
